import { GraphError } from '../graph/client';
import { isNetworkFailure } from '../api/offline';
import { formatDateTime } from '../ui/dates';

/**
 * B4 — offline task comments and files (owner rulings 2026-10-25,
 * docs/beta-features.md): on a task page that loses signal, a comment or a
 * photo/PDF is kept on this device and sent when the connection returns.
 * Completing a task still needs a connection (his ruling).
 *
 * - Tied to the account that wrote it (shared devices, like audits —
 *   src/local/ownership.ts): only that account sees it or sends it.
 * - Every send is safe to repeat: comments carry their ClientKey, files an
 *   upload key (api/src/files.ts), so an answer lost on the way back never
 *   makes a second comment, email or file.
 * - Sent comments get a line "Written without signal at …" (his ruling: the
 *   feed shows when it reached the server; the device clock is never trusted
 *   for the record).
 * - What can't be delivered (task deleted, access lost, refused, older than
 *   72 hours) is KEPT, marked "Couldn't send" with the reason, until the
 *   person discards it (his ruling) — the comment text can be copied.
 */

export const OUTBOX_EXPIRY_MS = 72 * 3_600_000;
/** The note added to a sent comment (and the room it needs under the 10,000-character Body cap). */
export const BODY_LIMIT = 10_000;
/** How long an item queued after an unanswered online try waits before its first send. */
export const AFTER_FAILED_TRY_MS = 20_000;
/** Unexpected (non-server, non-network) errors: give up after this many tries with the error's own words. */
export const MAX_UNEXPECTED_TRIES = 5;

export type OutboxKind = 'comment' | 'file';

export interface OutboxItem {
  id: string;
  /** The signed-in account that wrote it, lower case. */
  ownerUpn: string;
  taskId: number;
  storeId: number;
  /** Shown on the Tasks page list. */
  taskTitle: string;
  kind: OutboxKind;
  /** Device time it was written (shown, and the 72-hour limit — never sent as the record's time). */
  writtenAt: string;
  /** The typed name behind a shared store account (else the person's own). */
  authorName: string;
  /** Comment text (comments). */
  body?: string;
  /** Extra people to email (comments). */
  notifyUpns?: string[];
  /** File: photo or pdf; the name the upload carries; the person's own name for display ('' = none). */
  fileKind?: 'photo' | 'pdf';
  fileName?: string;
  fileTitle?: string;
  /** ClientKey (comments) / upload key (files) — the same on every resend. */
  key: string;
  /** Files: the ClientKey of the "Added …" timeline row. */
  eventKey?: string;
  /** Files: the upload landed; only the timeline row is left. */
  uploaded?: string;
  /**
   * Not sent before this time (ms): an item queued after an online try got
   * no answer — that request may still be running on the server, and a
   * resend racing it could make a second comment (QA 2026-10-25).
   */
  notBefore?: number;
  state: 'waiting' | 'failed';
  /** Why it couldn't be sent (failed). */
  error?: string;
  attempts: number;
}

export interface OutboxBackend {
  list(): Promise<OutboxItem[]>;
  put(item: OutboxItem): Promise<void>;
  delete(id: string): Promise<void>;
  getBlob(id: string): Promise<Blob | undefined>;
  putBlob(id: string, blob: Blob): Promise<void>;
  deleteBlob(id: string): Promise<void>;
}

/** How a queued item reaches the server (src/data/taskOutboxSender.ts). */
export interface OutboxSender {
  postComment(item: OutboxItem, body: string): Promise<void>;
  /** Returns the name to show in the "Added …" timeline row. */
  uploadFile(item: OutboxItem, blob: Blob): Promise<string>;
  addFileEvent(item: OutboxItem, shownName: string): Promise<void>;
}

const lower = (s: string) => s.trim().toLowerCase();

/** "Oct 25, 2026, 2:14 PM ET" — company time, labelled (2026-10-28). */
export function writtenLabel(iso: string): string {
  return formatDateTime(iso);
}

/** The comment as sent: the text + "(Written without signal at …)", the text cut to fit the cap. */
export function offlineBody(body: string, writtenAt: string): string {
  const when = writtenLabel(writtenAt);
  const note = when ? `\n\n(Written without signal at ${when})` : '\n\n(Written without signal)';
  // The server counts UTF-16 units (String.length): cut whole characters until it fits (QA 2026-10-25).
  let text = body;
  if (text.length + note.length > BODY_LIMIT) {
    const chars = [...text];
    let len = text.length;
    while (chars.length > 0 && len + note.length > BODY_LIMIT) len -= chars.pop()!.length;
    text = chars.join('').trimEnd();
  }
  return text + note;
}

/** Why a failed send can't be retried, in the person's words — or null when it should simply wait and retry. */
export function permanentReason(err: unknown): string | null {
  if (isNetworkFailure(err)) return null;
  if (err instanceof GraphError) {
    if (err.status === 404) return 'The task was deleted, or this account can no longer see it.';
    if (err.status === 429 || err.status >= 500) return null;
    if (err.status === 401) return null; // sign-in refresh — try again later
    return err.message || `Refused (${err.status}).`;
  }
  return null;
}

let counter = 0;
function newId(now: number): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  counter += 1;
  return `tq-${now.toString(36)}-${counter}-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

const blobKey = (id: string) => `tq:${id}`;

/** Past its 72 hours (a file whose upload landed only has its timeline row left — never expires). */
export function isExpired(item: OutboxItem, now: number): boolean {
  if (item.uploaded) return false;
  const written = Date.parse(item.writtenAt);
  return Number.isNaN(written) || now - written > OUTBOX_EXPIRY_MS;
}

export interface OutboxStatus {
  /** This account's queued items, oldest first. */
  items: OutboxItem[];
  /** A send is running. */
  sending: boolean;
}

export class TaskOutbox {
  private owner: string | undefined;
  private ownerRequired = false;
  private listeners = new Set<(s: OutboxStatus) => void>();
  private status: OutboxStatus = { items: [], sending: false };
  private running = false;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  /**
   * Which sign-in the browser would send with NOW (MSAL's active account id —
   * shared by every tab), and which one it was when this tab named its owner.
   * Another tab can switch accounts without this one reloading (security
   * review 2026-10-25): then nothing is sent until this tab catches up.
   */
  private currentAccount: (() => string | null) | null = null;
  private ownerAccount: string | null = null;

  constructor(
    private backend: OutboxBackend,
    private sender: OutboxSender,
    private isOnline: () => boolean = () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    private now: () => number = Date.now,
  ) {}

  /** Only this account's items are shown and sent from now on. */
  setOwner(signedInUpn: string): void {
    this.owner = lower(signedInUpn);
    this.ownerAccount = this.currentAccount?.() ?? null;
    void this.refresh();
  }

  /** Check the browser's current sign-in before every send (call before setOwner). */
  setAccountCheck(currentAccount: () => string | null): void {
    this.currentAccount = currentAccount;
  }

  /** The browser still signs in as the account this tab named (unknown = don't block). */
  private sameSignIn(): boolean {
    if (!this.currentAccount || this.ownerAccount === null) return true;
    return this.currentAccount() === this.ownerAccount;
  }

  /** Fail closed: until setOwner(), nothing is shown or sent. */
  requireOwner(): void {
    this.ownerRequired = true;
  }

  private mine(item: OutboxItem): boolean {
    if (this.owner === undefined) return !this.ownerRequired;
    return this.owner !== '' && lower(item.ownerUpn) === this.owner;
  }

  subscribe(listener: (s: OutboxStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  getStatus(): OutboxStatus {
    return this.status;
  }

  private emit(next: Partial<OutboxStatus>): void {
    this.status = { ...this.status, ...next };
    for (const l of this.listeners) l(this.status);
  }

  /** Re-read this account's items for the screens. */
  async refresh(): Promise<void> {
    const items = await this.backend.list().catch(() => [] as OutboxItem[]);
    this.emit({ items: items.filter((i) => this.mine(i)).sort((a, b) => a.writtenAt.localeCompare(b.writtenAt) || a.id.localeCompare(b.id)) });
  }

  private requireMe(): string {
    if (!this.owner) throw new Error('Not signed in.');
    return this.owner;
  }

  async enqueueComment(input: {
    taskId: number;
    storeId: number;
    taskTitle: string;
    body: string;
    authorName: string;
    notifyUpns: string[];
    clientKey: string;
    /** An online try just got no answer: hold the first resend back (AFTER_FAILED_TRY_MS). */
    afterFailedTry?: boolean;
  }): Promise<OutboxItem> {
    const now = this.now();
    const item: OutboxItem = {
      id: newId(now),
      ownerUpn: this.requireMe(),
      taskId: input.taskId,
      storeId: input.storeId,
      taskTitle: input.taskTitle,
      kind: 'comment',
      writtenAt: new Date(now).toISOString(),
      authorName: input.authorName,
      body: input.body,
      notifyUpns: input.notifyUpns,
      key: input.clientKey,
      ...(input.afterFailedTry ? { notBefore: now + AFTER_FAILED_TRY_MS } : {}),
      state: 'waiting',
      attempts: 0,
    };
    await this.backend.put(item);
    await this.refresh();
    this.kick(input.afterFailedTry ? AFTER_FAILED_TRY_MS + 100 : undefined);
    return item;
  }

  async enqueueFile(input: {
    taskId: number;
    storeId: number;
    taskTitle: string;
    authorName: string;
    fileKind: 'photo' | 'pdf';
    fileName: string;
    fileTitle?: string;
    blob: Blob;
    key: string;
    eventKey: string;
    afterFailedTry?: boolean;
  }): Promise<OutboxItem> {
    const now = this.now();
    const item: OutboxItem = {
      id: newId(now),
      ownerUpn: this.requireMe(),
      taskId: input.taskId,
      storeId: input.storeId,
      taskTitle: input.taskTitle,
      kind: 'file',
      writtenAt: new Date(now).toISOString(),
      authorName: input.authorName,
      fileKind: input.fileKind,
      fileName: input.fileName,
      fileTitle: input.fileTitle ?? '',
      key: input.key,
      eventKey: input.eventKey,
      ...(input.afterFailedTry ? { notBefore: now + AFTER_FAILED_TRY_MS } : {}),
      state: 'waiting',
      attempts: 0,
    };
    // Bytes first: an item never points at bytes that aren't there.
    await this.backend.putBlob(blobKey(item.id), input.blob);
    await this.backend.put(item);
    await this.refresh();
    this.kick(input.afterFailedTry ? AFTER_FAILED_TRY_MS + 100 : undefined);
    return item;
  }

  /** Remove one of this account's items (and its bytes). */
  async discard(id: string): Promise<void> {
    const item = (await this.backend.list()).find((i) => i.id === id);
    if (!item || !this.mine(item)) return;
    await this.remove(item);
    await this.refresh();
  }

  private async remove(item: OutboxItem): Promise<void> {
    await this.backend.delete(item.id);
    if (item.kind === 'file') await this.backend.deleteBlob(blobKey(item.id)).catch(() => undefined);
  }

  /** This account's items that couldn't be sent — removed at sign-out when the person agrees. */
  async discardFailed(): Promise<number> {
    const failed = (await this.backend.list()).filter((i) => this.mine(i) && i.state === 'failed');
    for (const i of failed) await this.remove(i);
    await this.refresh();
    return failed.length;
  }

  /** This account's items: still waiting / couldn't send (null = couldn't read). */
  async counts(): Promise<{ waiting: number; failed: number } | null> {
    try {
      const mine = (await this.backend.list()).filter((i) => this.mine(i));
      return { waiting: mine.filter((i) => i.state === 'waiting').length, failed: mine.filter((i) => i.state === 'failed').length };
    } catch {
      return null;
    }
  }

  /** Items still waiting on this device — every account's (moving the app to a new address would strand them). */
  async pendingItems(): Promise<number | null> {
    try {
      const now = this.now();
      return (await this.backend.list()).filter((i) => i.state === 'waiting' && !isExpired(i, now)).length;
    } catch {
      return null;
    }
  }

  /** Waiting items split into this account's and other accounts' (null = couldn't read). */
  async pendingSplit(): Promise<{ mine: number; others: number } | null> {
    try {
      const now = this.now();
      // Past 72 hours they'll never send — they don't hold anything up (QA 2026-10-25).
      const waiting = (await this.backend.list()).filter((i) => i.state === 'waiting' && !isExpired(i, now));
      const mine = waiting.filter((i) => this.mine(i)).length;
      return { mine, others: waiting.length - mine };
    } catch {
      return null;
    }
  }

  kick(delayMs = 300): void {
    if (this.running) this.again = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), delayMs);
  }

  start(): void {
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline);
    this.interval = setInterval(() => void this.flush(), 30_000);
    this.kick(0);
  }

  stop(): void {
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline);
    if (this.interval) clearInterval(this.interval);
    if (this.timer) clearTimeout(this.timer);
  }

  private onOnline = () => this.kick(0);

  /** Send this account's waiting items, oldest first. Stops at the first sign of no signal. */
  async flush(): Promise<void> {
    if (this.running) {
      this.again = true;
      return;
    }
    if (this.ownerRequired && !this.owner) return;
    this.running = true;
    try {
      // One sender per browser: every tab's outbox reads the same database,
      // and two tabs posting the same key at once can both create (QA
      // 2026-10-25). Another tab holds the lock → it is sending; skip.
      const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
      if (locks) {
        await locks.request('ssp-auditplus-outbox', { ifAvailable: true }, async (lock) => {
          if (lock) await this.flushLocked();
        });
      } else {
        await this.flushLocked();
      }
    } finally {
      this.running = false;
      this.emit({ sending: false });
    }
  }

  private async flushLocked(): Promise<void> {
    {
      do {
        this.again = false;
        const all = await this.backend.list().catch(() => [] as OutboxItem[]);
        const waiting = all
          .filter((i) => this.mine(i) && i.state === 'waiting')
          .sort((a, b) => a.writtenAt.localeCompare(b.writtenAt) || a.id.localeCompare(b.id));
        if (waiting.length === 0) break;
        // Expired first — they never go out late (his ruling: kept and shown).
        const live: OutboxItem[] = [];
        for (const item of waiting) {
          if (isExpired(item, this.now())) {
            await this.backend.put({ ...item, state: 'failed', error: 'Not sent within 72 hours of being written.' });
          } else live.push(item);
        }
        if (live.length > 0 && this.isOnline()) {
          this.emit({ sending: true });
          for (const item of live) {
            // Another tab signed in as someone else: send nothing until this one catches up.
            if (!this.sameSignIn()) break;
            // Held back after an unanswered online try: its own kick sends it later.
            if (item.notBefore !== undefined && this.now() < item.notBefore) continue;
            if (!(await this.sendOne(item))) break;
          }
        }
        await this.refresh();
      } while (this.again);
    }
  }

  /** The item as stored now — undefined once it was sent or discarded (never write it back then). */
  private async current(id: string): Promise<OutboxItem | undefined> {
    return (await this.backend.list().catch(() => [] as OutboxItem[])).find((i) => i.id === id);
  }

  /** false = stop this run (no signal / server busy); true = go on with the next item. */
  private async sendOne(item: OutboxItem): Promise<boolean> {
    try {
      if (item.kind === 'comment') {
        await this.sender.postComment(item, offlineBody(item.body ?? '', item.writtenAt));
        await this.remove(item);
        return true;
      }
      let shown = item.uploaded;
      if (!shown) {
        const blob = await this.backend.getBlob(blobKey(item.id));
        if (!blob) {
          await this.backend.put({ ...item, state: 'failed', error: 'The file is no longer saved on this device.' });
          return true;
        }
        shown = (await this.sender.uploadFile(item, blob)) || item.fileTitle || item.fileName || 'file';
        // Landed: from here only the timeline row is left (never upload again).
        const now = await this.current(item.id);
        if (now) await this.backend.put({ ...now, uploaded: shown });
        await this.backend.deleteBlob(blobKey(item.id)).catch(() => undefined);
      }
      try {
        await this.sender.addFileEvent(item, shown);
      } catch (err) {
        // The row is best-effort (as online): retry only when there was no signal.
        if (isNetworkFailure(err)) throw err;
      }
      await this.backend.delete(item.id);
      return true;
    } catch (err) {
      // Discarded meanwhile: leave it gone (QA 2026-10-25).
      const current = await this.current(item.id);
      if (!current) return true;
      const attempts = current.attempts + 1;
      let reason = permanentReason(err);
      // Neither the server nor the network (a sign-in error, a bug): give up
      // after a few tries with its own words, instead of holding everything
      // behind it for 72 hours.
      if (reason === null && !isNetworkFailure(err) && !(err instanceof GraphError) && attempts >= MAX_UNEXPECTED_TRIES) {
        reason = err instanceof Error && err.message ? err.message : 'Couldn’t be sent.';
      }
      if (reason !== null) {
        await this.backend.put({ ...current, state: 'failed', error: reason, attempts });
        return true;
      }
      await this.backend.put({ ...current, attempts }).catch(() => undefined);
      return false;
    }
  }
}

/** In-memory backend for unit tests. */
export function createMemoryOutboxBackend(): OutboxBackend & { blobs: Map<string, Blob> } {
  const items = new Map<string, OutboxItem>();
  const blobs = new Map<string, Blob>();
  return {
    blobs,
    list: () => Promise.resolve([...items.values()].map((i) => structuredClone(i))),
    put: (i) => {
      items.set(i.id, structuredClone(i));
      return Promise.resolve();
    },
    delete: (id) => {
      items.delete(id);
      return Promise.resolve();
    },
    getBlob: (id) => Promise.resolve(blobs.get(id)),
    putBlob: (id, b) => {
      blobs.set(id, b);
      return Promise.resolve();
    },
    deleteBlob: (id) => {
      blobs.delete(id);
      return Promise.resolve();
    },
  };
}
